import { useState } from 'react';
import { Loader2, Download } from 'lucide-react';
import { Modal } from './Modal';
import formStyles from './Form.module.css';
import { generateLocationQrBulkPdf } from '@/lib/qr-pdf';
import styles from './QrModal.module.css';

/**
 * Bulk per-location QR PDF modal (commit 13 of third-party-charger-
 * access ticket). Lists all chargers at a location with checkboxes
 * (default all selected) and generates a single multi-page PDF on
 * download — one charger per page, A4 portrait. Triggered from the
 * toolbar button on LocationDetailPage.
 *
 * No PNG bulk-download. Drivers don't need a zip of PNGs; the
 * common operator workflow is "print these all at once" which the
 * PDF covers cleanly.
 */

interface BulkCharger {
  _id: string;
  identity: string;
  vendor?: string;
  model?: string;
}

interface QrBulkModalProps {
  chargers: BulkCharger[];
  locationId: string;
  locationName: string;
  onClose: () => void;
}

export function QrBulkModal({
  chargers, locationId, locationName, onClose,
}: QrBulkModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(chargers.map((c) => c._id)),
  );
  const [busy, setBusy] = useState(false);

  const toggle = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = selectedIds.size === chargers.length;
  const setAll = (on: boolean): void => {
    setSelectedIds(on ? new Set(chargers.map((c) => c._id)) : new Set());
  };

  const selectedChargers = chargers.filter((c) => selectedIds.has(c._id));

  const handleDownload = async (): Promise<void> => {
    if (selectedChargers.length === 0) return;
    setBusy(true);
    try {
      const blob = await generateLocationQrBulkPdf({
        chargers: selectedChargers.map((c) => ({ identity: c.identity })),
        locationId,
        locationName,
      });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${slugify(locationName)}-qr-codes.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 500);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Download QR codes — ${locationName}`}
      size="md"
    >
      <div className={styles.bulkWrap}>
        <p className={styles.bulkHint}>
          One QR per page, A4 portrait. Each page carries the charger
          identity, location name, and "Scan to start charging" copy.
          Print on adhesive sticker paper for the field.
        </p>

        <div className={styles.bulkSelectAllRow}>
          <span>{selectedIds.size} of {chargers.length} selected</span>
          <button
            type="button"
            className={styles.bulkSelectAllBtn}
            onClick={() => setAll(!allSelected)}
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
        </div>

        <div className={styles.bulkList}>
          {chargers.length === 0 && (
            <p className={styles.bulkHint} style={{ padding: '12px 8px' }}>
              No chargers at this location yet.
            </p>
          )}
          {chargers.map((c) => (
            <label key={c._id} className={styles.bulkRow}>
              <input
                type="checkbox"
                checked={selectedIds.has(c._id)}
                onChange={() => toggle(c._id)}
              />
              <span className={styles.bulkRowIdentity}>{c.identity}</span>
              {(c.vendor || c.model) && (
                <span className={styles.bulkRowMeta}>
                  {[c.vendor, c.model].filter(Boolean).join(' ')}
                </span>
              )}
            </label>
          ))}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={formStyles.btnSecondary}
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={formStyles.btnPrimary}
            onClick={() => void handleDownload()}
            disabled={busy || selectedChargers.length === 0}
          >
            {busy
              ? <><Loader2 size={13} className={styles.spin} /> Building PDF…</>
              : <><Download size={13} /> PDF ({selectedChargers.length})</>}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function slugify(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'location';
}
